<template>
    <div>
        <h3 class="text-header font-bold text-lg">Pay for your order</h3>
        <p class="text-body/80 text-sm mt-1">
            Total due: <span class="text-header font-bold">{{ formatMoney(amount, currency) }}</span>
        </p>
    </div>
    <div class="mt-8 relative space-y-6 mx-auto min-w-80 w-full md:w-6/12">

        <div v-if="loading" class="flex items-center gap-2 text-body/80 text-sm">
            <span class="h-4 w-4 rounded-full border-2 border-brand-500 border-t-transparent animate-spin"></span>
            Loading payment options…
        </div>

        <p v-else-if="!options.length" class="text-sm text-header bg-muted/5 border border-app rounded-xl p-4">
            Online payment isn't available for {{ currency }} yet. Please contact the organizer to complete your order.
        </p>

        <template v-else>
            <!-- Gateway -->
            <div v-if="options.length > 1" class="space-y-1.5">
                <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Pay with</span>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <button v-for="p in options" :key="p.name" type="button" :disabled="waiting" @click="selectProvider(p.name)"
                        class="text-left px-3.5 py-2.5 rounded-xl border transition-all disabled:opacity-60"
                        :class="provider === p.name ? 'border-brand-500 bg-brand-500/10' : 'border-app'">
                        <span class="block text-sm font-bold text-header">{{ p.label }}</span>
                        <span class="block text-xs text-body/80 truncate">{{ p.options.map((o) => o.label).join(' · ') }}</span>
                    </button>
                </div>
            </div>

            <!-- That gateway's options -->
            <div v-if="current" class="w-full block space-y-1.5">
                <div class="w-full text-xs font-bold text-body/80 uppercase tracking-wider">
                    {{ options.length > 1 ? 'Payment option' : 'Pay with ' + current.label }}
                </div>
                <div class="w-full block sm:inline-flex flex-wrap items-center rounded-2xl border border-app bg-muted/5 p-1">
                    <button v-for="o in current.options" :key="o.key" type="button" :disabled="waiting" @click="selectOption(o.key)"
                        class="px-4 py-2.5 w-full rounded-xl justify-center text-sm font-bold transition-all flex items-center gap-2 disabled:opacity-60 flex-1"
                        :class="option?.key === o.key ? 'bg-brand-500 text-on-brand shadow' : 'text-body/80'">
                        <component :is="optionIcons[o.key] || CreditCard" class="h-4 w-4" /> {{ o.label }}
                    </button>
                </div>
                <p v-if="option?.description" class="text-body/80 text-sm">{{ option.description }}</p>
            </div>

            <!-- Push: prompt on the buyer's phone -->
            <div v-if="option?.flow === 'push'" class="w-full space-y-4">
                <label class="block max-w-full">
                    <span class="text-xs font-bold text-body/80 uppercase tracking-wider">Phone number</span>
                    <PhoneInput v-model.trim="phone" type="tel" placeholder="e.g. 0754 123 456" :class="[]" class="form-control w-full" />
                </label>
                <button type="button" :disabled="busy || !phone" @click="payPush" class="w-full justify-center" :class="primaryClass">
                    <span v-if="busy" class="h-4 w-4 rounded-full  border-2 border-white border-t-transparent animate-spin"></span>
                    {{ busy ? 'Sending…' : instructions ? 'Resend request' : 'Send payment request' }}
                </button>
                <p v-if="instructions" class="text-sm text-header bg-brand-500/10 rounded-xl p-3">
                    {{ instructions }} Waiting for confirmation…
                </p>
            </div>

            <!-- Checkout: the gateway's page in a new tab -->
            <div v-else-if="option?.flow === 'checkout'" class="space-y-4">
                <button v-if="!waiting" type="button" :disabled="busy" @click="payCheckout" class="w-full justify-center" :class="primaryClass">
                    <span v-if="busy" class="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                    {{ busy ? 'Opening…' : 'Continue to ' + (current?.label || 'payment') }}
                </button>
                <div v-else class="text-sm text-header bg-brand-500/10 rounded-xl p-3 space-y-1">
                    <p>Complete the payment in the new tab. This page updates once it's confirmed.</p>
                    <p class="text-body/80 text-xs">
                        Tab didn't open? <a :href="checkoutUrl" target="_blank" rel="noopener" class="underline font-semibold">Open the payment page</a>
                        · <button type="button" class="underline font-semibold" @click="cancelWaiting">Choose another option</button>
                    </p>
                </div>
            </div>

            <!-- QR: the same page, scanned from another phone -->
            <div v-else-if="option?.flow === 'qr'" class="w-full space-y-4">
                <button v-if="!qrCode" type="button" :disabled="busy" @click="payQr" class="w-full justify-center" :class="primaryClass">
                    <span v-if="busy" class="h-4 w-full block rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                    {{ busy ? 'Generating…' : 'Show QR code' }}
                </button>
                <div v-else class="flex flex-col items-center gap-3">
                    <img :src="qrCode" alt="Payment QR code" class="w-48 h-48 rounded-2xl border border-app bg-white p-2" />
                    <p class="text-body/80 text-xs">Waiting for payment…</p>
                </div>
            </div>
        </template>

        <p v-if="error" class="text-sm text-red-500 font-semibold">{{ error }}</p>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Smartphone, CreditCard, QrCode, Wallet, Landmark, Hash } from 'lucide-vue-next';
import { formatMoney } from '../helpers';
import { fetchEventPaymentOptions, createEventPayment, checkEventPaymentStatus } from '~/utils/api';
import PhoneInput from '@core/forms/PhoneInput.vue';

const props = defineProps<{ orderId: string; amount: number; currency: string; contactPhone?: string | null }>();
const emit = defineEmits<{ (e: 'paid'): void }>();

const fieldClass =
    'mt-1.5 w-full rounded-xl border border-app bg-card/70 px-3.5 py-2.5 text-sm text-header outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all';
const primaryClass =
    'px-6 py-3 rounded-2xl bg-brand-500 text-on-brand font-bold hover:bg-brand-600 transition-all active:scale-95 disabled:opacity-40 flex items-center gap-2';

// flow decides the UI: push = prompt on the buyer's phone, checkout = the
// gateway's page in a new tab, qr = that page as a QR code.
type GatewayOption = { key: string; flow: 'push' | 'checkout' | 'qr'; label: string; description: string };
type Gateway = { name: string; label: string; options: GatewayOption[] };

const optionIcons: Record<string, any> = {
    mobileMoney: Smartphone,
    mobileMoneyCheckout: Smartphone,
    card: CreditCard,
    bankTransfer: Landmark,
    ussd: Hash,
    paypal: Wallet,
    qr: QrCode,
};

// Gateways and their options come from the server, filtered to the
// configured gateways and what each offers in this order's currency.
const options = ref<Gateway[]>([]);
const loading = ref(true);
const provider = ref('');
const optionKey = ref('');
const current = computed(() => options.value.find((p) => p.name === provider.value));
const option = computed(() => current.value?.options.find((o) => o.key === optionKey.value));

const phone = ref(props.contactPhone || '');
const busy = ref(false);
const waiting = ref(false);
const error = ref('');
const instructions = ref('');
const qrCode = ref('');
const checkoutUrl = ref('');

onMounted(async () => {
    try {
        const res = await fetchEventPaymentOptions({ orderId: props.orderId });
        console.log("fetchEventPaymentOptions", res)
        options.value = res?.providers || [];
    } catch (e) {
        console.error('Payment options failed', e);
    } finally {
        loading.value = false;
    }

    if (options.value.length) selectProvider(options.value[0].name);
});

// Mobile money is approved off-band (on the buyer's phone), hosted checkouts
// happen in another tab, and PayPal only captures once we verify — so poll
// until the gateway confirms. Gives up after 15 minutes.
const POLL_INTERVAL = 4000;
const POLL_LIMIT = 15 * 60 * 1000;
let pollTimer: number | null = null;
const stopPolling = () => {
    if (pollTimer) {
        window.clearInterval(pollTimer);
        pollTimer = null;
    }
};
onBeforeUnmount(stopPolling);

const pollStatus = (paymentId: string) => {
    stopPolling();
    const startedAt = Date.now();

    pollTimer = window.setInterval(async () => {
        if (Date.now() - startedAt > POLL_LIMIT) {
            stopPolling();
            waiting.value = false;
            error.value = "We haven't received a confirmation yet. If you were charged, contact the organizer with your order number.";
            return;
        }

        const res = await checkEventPaymentStatus({ paymentId });
        const status = res?.data?.status;

        if (status === 'succeeded' || status === 'SUCCESS') {
            stopPolling();
            waiting.value = false;
            emit('paid');
        } else if (status === 'failed' || status === 'canceled' || status === 'FAIL') {
            stopPolling();
            waiting.value = false;
            instructions.value = '';
            qrCode.value = '';
            error.value = res?.data?.message || 'Payment failed. Please try again.';
        }
    }, POLL_INTERVAL);
};

const selectProvider = (name: string) => {
    provider.value = name;
    selectOption(current.value?.options[0]?.key || '');
};

const selectOption = (key: string) => {
    optionKey.value = key;
    error.value = '';
    instructions.value = '';
    qrCode.value = '';
};

const cancelWaiting = () => {
    stopPolling();
    waiting.value = false;
};

const startPayment = (extra: Record<string, any> = {}) =>
    createEventPayment({
        orderId: props.orderId,
        provider: provider.value,
        option: optionKey.value,
        // Hosted checkouts send the buyer back here (the new tab).
        returnUrl: window.location.origin + window.location.pathname,
        ...extra,
    });

const payPush = async () => {
    if (!phone.value) return;
    busy.value = true;
    error.value = '';

    try {
        const res = await startPayment({ phone: phone.value });
        if (!res || res.status === false) throw new Error(res?.message || 'Could not send the payment request');
        instructions.value = res.data?.instructions || 'Check your phone and enter your PIN to approve.';
        pollStatus(res.data.paymentId);
    } catch (e: any) {
        error.value = e?.message || 'Could not send the payment request';
    } finally {
        busy.value = false;
    }
};

const payCheckout = async () => {
    busy.value = true;
    error.value = '';

    // Open the tab now, inside the click, so popup blockers allow it; it's
    // pointed at the checkout once the gateway returns its URL.
    const tab = window.open('', '_blank');

    try {
        const res = await startPayment();
        if (!res || res.status === false) throw new Error(res?.message || 'Could not start checkout');

        checkoutUrl.value = res.data?.checkoutUrl || '';
        if (!checkoutUrl.value) throw new Error('Could not start checkout');

        if (tab) {
            tab.location.href = checkoutUrl.value;
            waiting.value = true;
            pollStatus(res.data.paymentId);
        } else {
            // Popup blocked: pay in this tab instead.
            window.location.href = checkoutUrl.value;
        }
    } catch (e: any) {
        tab?.close();
        error.value = e?.message || 'Could not start checkout';
    } finally {
        busy.value = false;
    }
};

const payQr = async () => {
    busy.value = true;
    error.value = '';

    try {
        const res = await startPayment();
        if (!res || res.status === false) throw new Error(res?.message || 'Could not generate a QR code');
        qrCode.value = res.data?.qrCode || '';
        if (!qrCode.value) throw new Error('Could not generate a QR code');
        pollStatus(res.data.paymentId);
    } catch (e: any) {
        error.value = e?.message || 'Could not generate a QR code';
    } finally {
        busy.value = false;
    }
};
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
